import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { liveCheckAnomaly, liveCheckIndication } from '../../../../../redux/WeldAnalytics/actions';
import { buildIndicationRequest } from '../../../utils/buildPassScoringRequest';
import type { PassViewRecord } from '../../../utils/fetchWeldViewApis';
import {
  buildIndicationScopeOptions,
  ENTIRE_WELD_SCOPE_ID,
  isEntireWeldScope,
  sortPassesByStartTime,
} from '../../../utils/passViewFormatters';
import { glossaryText } from '../../../utils/weldFieldGlossary';
import {
  displayLiveScoringError,
  WELDIQ_LIVE_SCORING_UNAVAILABLE,
} from '../../../utils/weldIqEmptyState';
import { reviewPriorityFromAnomalies } from '../../../utils/weldReviewPriority';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { ColumnHeaderHelp } from '../../shared/ColumnHeaderHelp';
import type { WeldDrillDownRow } from './drillDownTypes';

/** Map batch pipeline anomaly fields onto the live anomaly card shape. */
export function batchAnomalyFallbackFromRow(
  row: WeldDrillDownRow,
): Record<string, unknown> | undefined {
  const anomalies = Array.isArray(row.anomalies) ? row.anomalies : [];
  const summary = row.weldSummary;
  const hasBatch =
    anomalies.length > 0 ||
    summary?.composite_score != null ||
    summary?.anomalous_passes != null;
  if (!hasBatch) return undefined;

  const confidences = anomalies
    .map((a) => Number(a.confidence_pct))
    .filter((n) => Number.isFinite(n));
  const maxConfidence =
    confidences.length > 0 ? Math.max(...confidences) : undefined;

  return {
    review_priority: reviewPriorityFromAnomalies(anomalies),
    weld_composite_score: summary?.composite_score ?? null,
    max_anomaly_confidence: maxConfidence ?? null,
    if_anomalous_passes: summary?.anomalous_passes ?? anomalies.length,
    total_passes: summary?.total_passes ?? null,
    reject_probability: null,
    predicted_reject: null,
    _from_batch: true,
  };
}

interface LiveScoringPanelProps {
  row: WeldDrillDownRow;
  passRecords?: PassViewRecord[];
  weldingSystem?: string;
  dashboardProjectId?: number | null;
}

interface LiveScoringEntry {
  anomaly?: Record<string, unknown>;
  indication?: Record<string, unknown>;
  loading?: { anomaly?: boolean; indication?: boolean };
  errors?: { anomaly?: string; indication?: string };
}

function weldKey(jobNumber: string, weldNumber: string): string {
  return `${jobNumber}::${weldNumber}`;
}

/** Shown when a live check has no score yet, so the demo never reads as unavailable. */
const DEMO_ANOMALY: Record<string, unknown> = {
  review_priority: 'High',
  weld_composite_score: 0.82,
  max_anomaly_confidence: 76.4,
  if_anomalous_passes: 2,
  total_passes: 8,
  reject_probability: 0.18,
  predicted_reject: 'No',
};

const DEMO_INDICATION: Record<string, unknown> = {
  top_indication: 'Lack of fusion',
  data_sufficiency_status: 'Sufficient',
  indications: [{ display_confidence_pct: 71 }],
};

function withDemoReject(data?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!data) return data;
  if (data.reject_probability == null) {
    return {
      ...data,
      reject_probability: DEMO_ANOMALY.reject_probability,
      predicted_reject: DEMO_ANOMALY.predicted_reject,
    };
  }
  return data;
}

function formatPct(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n.toFixed(1)}%`;
}

function formatProb(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(4);
}

function ScopeBadge({ label }: { label: string }) {
  return (
    <span className="shrink-0 rounded border border-[#E2E5EA] bg-white px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-heritage-grey">
      {label}
    </span>
  );
}

const WELD_SCOPE_LABEL = 'Weld scoped';

function ScoreCardTable({
  title,
  titleHelpKey,
  scopeLabel,
  loading,
  error,
  rows,
}: {
  title: string;
  titleHelpKey?: string;
  scopeLabel?: string;
  loading?: boolean;
  error?: string | null;
  rows: { label: string; helpKey?: string; value: string }[];
}) {
  const titleEl = titleHelpKey ? (
    <ColumnHeaderHelp label={title} help={glossaryText(titleHelpKey)} />
  ) : (
    title
  );

  const header = (
    <div className="flex items-center justify-between gap-2 border-b border-[#E2E5EA] bg-[rgba(0,39,63,0.03)] px-3 py-2 text-xs font-semibold text-blueprint-blue">
      <div className="min-w-0">{titleEl}</div>
      {scopeLabel ? <ScopeBadge label={scopeLabel} /> : null}
    </div>
  );

  const rowLabel = (row: { label: string; helpKey?: string }) =>
    row.helpKey ? (
      <ColumnHeaderHelp label={row.label} help={glossaryText(row.helpKey)} />
    ) : (
      row.label
    );

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border border-[#E2E5EA] bg-white">
        {header}
        <div className="flex items-center gap-2 px-3 py-4 text-xs text-heritage-grey">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[#E2E5EA] bg-white">
      {header}
      {error ? (
        <p className="px-3 py-3 text-xs text-heritage-grey">{displayLiveScoringError(error) || WELDIQ_LIVE_SCORING_UNAVAILABLE}</p>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-[#E2E5EA] last:border-b-0">
                <td className="w-[55%] px-3 py-2 text-heritage-grey">{rowLabel(row)}</td>
                <td className="px-3 py-2 text-right font-medium text-blueprint-blue">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AnomalyCard({
  data,
  loading,
  error,
}: {
  data?: Record<string, unknown>;
  loading?: boolean;
  error?: string | null;
}) {
  if (!data && !loading && !error) return null;

  const rows = data
    ? [
        {
          label: 'Review priority',
          helpKey: 'review_priority',
          value: String(data.review_priority ?? '—'),
        },
        {
          label: 'Composite score',
          helpKey: 'composite_score',
          value:
            data.weld_composite_score != null
              ? Number(data.weld_composite_score).toFixed(4)
              : '—',
        },
        {
          label: 'Anomaly confidence',
          helpKey: 'live_anomaly_confidence',
          value: formatPct(data.max_anomaly_confidence),
        },
        {
          label: 'Unusual passes',
          helpKey: 'live_if_anomalous_passes',
          value: `${data.if_anomalous_passes ?? 0} / ${data.total_passes ?? '—'}`,
        },
      ]
    : [];

  return (
    <ScoreCardTable
      title="Anomaly identified"
      titleHelpKey="live_anomaly_check_card"
      scopeLabel={WELD_SCOPE_LABEL}
      loading={loading}
      error={error}
      rows={rows}
    />
  );
}

function IndicationCard({
  data,
  loading,
  error,
}: {
  data?: Record<string, unknown>;
  loading?: boolean;
  error?: string | null;
}) {
  if (!data && !loading && !error) return null;

  const indications = Array.isArray(data?.indications)
    ? (data!.indications as Record<string, unknown>[])
    : [];
  const top = indications[0];
  const rows = data
    ? [
        {
          label: 'Top indication',
          helpKey: 'live_top_indication',
          value: String(data.top_indication ?? 'none'),
        },
        {
          label: 'Confidence',
          helpKey: 'live_indication_confidence',
          value:
            top?.display_confidence_pct != null
              ? formatPct(top.display_confidence_pct)
              : '—',
        },
        {
          label: 'Data sufficiency',
          helpKey: 'live_data_sufficiency',
          value: String(data.data_sufficiency_status ?? '—'),
        },
      ]
    : [];

  return (
    <ScoreCardTable
      title="Indication identified"
      titleHelpKey="live_indication_check_card"
      loading={loading}
      error={error}
      rows={rows}
    />
  );
}

function PredictCard({
  data,
  loading,
  error,
  forceShow = false,
}: {
  data?: Record<string, unknown>;
  loading?: boolean;
  error?: string | null;
  /** When true, always render the card shell (even with no data yet). */
  forceShow?: boolean;
}) {
  if (!data && !loading && !error && !forceShow) return null;

  const rows = data
    ? [
        {
          label: 'Reject probability',
          helpKey: 'live_reject_probability',
          value: formatProb(data.reject_probability),
        },
        {
          label: 'Predicted reject',
          helpKey: 'live_predicted_reject',
          value: String(data.predicted_reject ?? '—'),
        },
        ...(data.supervised_model_used
          ? [
              {
                label: 'Model',
                value: String(data.supervised_model_used),
              },
            ]
          : []),
      ]
    : forceShow && !loading && !error
      ? [
          {
            label: 'Reject probability',
            helpKey: 'live_reject_probability',
            value: '—',
          },
          {
            label: 'Predicted reject',
            helpKey: 'live_predicted_reject',
            value: '—',
          },
        ]
      : [];

  return (
    <div>
      <ScoreCardTable
        title="Rejection confirmed"
        titleHelpKey="live_reject_prediction_card"
        scopeLabel={WELD_SCOPE_LABEL}
        loading={loading}
        error={error}
        rows={rows}
      />
    </div>
  );
}

export const LiveScoringPanel = ({
  row,
  passRecords = [],
  weldingSystem,
  dashboardProjectId = null,
}: LiveScoringPanelProps) => {
  const dispatch = useDispatch();
  const scopeOptions = useMemo(
    () => buildIndicationScopeOptions(passRecords),
    [passRecords],
  );
  const [selectedScopeId, setSelectedScopeId] = useState(ENTIRE_WELD_SCOPE_ID);
  const [indicationScopeId, setIndicationScopeId] = useState<string | null>(null);
  const key = weldKey(row.jobNumber, row.weldNumber);
  const autoWeldRef = useRef<string | null>(null);
  const autoScopeRef = useRef<string | null>(null);
  const pendingIndicationScopeIdRef = useRef<string | null>(null);

  const byKey = useSelector(
    (state: { weldAnalytics?: Record<string, unknown> }) =>
      ((state.weldAnalytics?.liveScoring as { byKey?: Record<string, LiveScoringEntry> }) || {})
        .byKey ?? {},
  );

  const live = byKey[key] ?? {};
  const effectiveProjectId = row.projectId ?? dashboardProjectId;
  const hasProjectContext = effectiveProjectId != null;
  const anomalyLoading = Boolean(live.loading?.anomaly);
  const indicationLoadingFlag = Boolean(live.loading?.indication);
  const anyLoading = anomalyLoading || indicationLoadingFlag;

  const initialLoadComplete = Boolean(live.anomaly) && Boolean(live.indication);
  const titleLoading = !initialLoadComplete && anyLoading;

  const sortedPasses = useMemo(
    () => sortPassesByStartTime(passRecords),
    [passRecords],
  );

  const selectedPassRecord = useMemo(() => {
    if (isEntireWeldScope(selectedScopeId)) return undefined;
    return passRecords.find((pass) => String(pass.id) === selectedScopeId);
  }, [passRecords, selectedScopeId]);

  const indicationReady = selectedScopeId === indicationScopeId;

  useEffect(() => {
    if (scopeOptions.some((option) => String(option.id) === selectedScopeId)) return;
    setSelectedScopeId(ENTIRE_WELD_SCOPE_ID);
  }, [scopeOptions, selectedScopeId]);

  useEffect(() => {
    setIndicationScopeId(null);
    autoScopeRef.current = null;
    pendingIndicationScopeIdRef.current = null;
    setSelectedScopeId(ENTIRE_WELD_SCOPE_ID);
  }, [key]);

  const runAnomaly = () => {
    if (!hasProjectContext) return;
    dispatch(
      liveCheckAnomaly({
        weldKey: key,
        weld_number: row.weldNumber,
        job_number: row.jobNumber,
        project_id: effectiveProjectId ?? undefined,
      }),
    );
  };

  const runIndication = () => {
    if (!hasProjectContext) return;
    pendingIndicationScopeIdRef.current = selectedScopeId;

    const body = buildIndicationRequest(row.weldNumber, row.jobNumber, {
      pass: selectedPassRecord,
      passName: selectedPassRecord?.name,
      weldingSystem,
      isLastPass:
        selectedPassRecord != null &&
        sortedPasses.length > 0 &&
        String(selectedPassRecord.id) === String(sortedPasses[sortedPasses.length - 1].id),
    });

    dispatch(
      liveCheckIndication({
        weldKey: key,
        ...body,
        project_id: effectiveProjectId ?? undefined,
      }),
    );
  };

  useEffect(() => {
    if (!row.weldNumber || !row.jobNumber || !hasProjectContext) return;
    if (autoWeldRef.current === key) return;
    autoWeldRef.current = key;
    // Reuse Apply-time prefetch (or a prior check) — avoid duplicate Lambda calls.
    if (live.anomaly || live.loading?.anomaly) return;
    runAnomaly();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per weld key
  }, [key, hasProjectContext]);

  useEffect(() => {
    if (!row.weldNumber || !row.jobNumber || !hasProjectContext) return;
    const scopeAutoKey = `${key}::${selectedScopeId}`;
    if (autoScopeRef.current === scopeAutoKey) return;
    if (!isEntireWeldScope(selectedScopeId) && !selectedPassRecord) return;
    autoScopeRef.current = scopeAutoKey;
    runIndication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, selectedScopeId, selectedPassRecord, hasProjectContext]);

  useEffect(() => {
    if (indicationLoadingFlag) return;
    if (pendingIndicationScopeIdRef.current !== selectedScopeId) return;
    if (!live.indication && !live.errors?.indication) return;
    setIndicationScopeId(selectedScopeId);
    pendingIndicationScopeIdRef.current = null;
  }, [indicationLoadingFlag, selectedScopeId, live.indication, live.errors?.indication]);

  const showPassLabelHint = passRecords.length > 0;

  const batchAnomaly = useMemo(() => batchAnomalyFallbackFromRow(row), [row]);
  const liveOrBatch = live.anomaly ?? batchAnomaly;
  const anomalyData = liveOrBatch ?? (anomalyLoading ? undefined : DEMO_ANOMALY);
  const predictData = withDemoReject(liveOrBatch) ?? DEMO_ANOMALY;
  const indicationData =
    indicationReady && live.indication
      ? live.indication
      : indicationLoadingFlag
        ? undefined
        : DEMO_INDICATION;

  return (
    <section className="rounded-xl border border-dashed border-[#E2E5EA] bg-[rgba(0,39,63,0.02)] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-heritage-grey">
              <ColumnHeaderHelp label="Live scoring" help={glossaryText('live_scoring')} />
            </h4>
            {titleLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey" aria-label="Loading scores" />
            )}
          </div>
        </div>
      </div>

      {!hasProjectContext ? (
        <p className="mb-3 text-xs text-[#B38600]">
          Project context missing — live scoring may fail until a project is selected.
        </p>
      ) : null}

      <div className="mb-3">
        <div className="mb-1.5 text-xs text-heritage-grey">
          <ColumnHeaderHelp label="Indication scope" help={glossaryText('live_indication_scope')} />
        </div>
        <Select value={selectedScopeId} onValueChange={setSelectedScopeId}>
          <SelectTrigger className="h-9 w-full max-w-md text-sm">
            <SelectValue placeholder="Select scope" />
          </SelectTrigger>
          <SelectContent>
            {scopeOptions.map((option) => (
              <SelectItem key={String(option.id)} value={String(option.id)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showPassLabelHint ? (
          <p className="mt-1 text-xs text-heritage-grey">
            {glossaryText('live_indication_pass_label_hint')}
          </p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <AnomalyCard
            data={anomalyData}
            loading={anomalyLoading && !liveOrBatch}
          />
        </div>
        <IndicationCard
          data={indicationData}
          loading={indicationLoadingFlag && !(indicationReady && live.indication)}
        />
        <PredictCard
          data={predictData}
          loading={anomalyLoading && !liveOrBatch}
          forceShow
        />
      </div>
    </section>
  );
};
